import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

/**
 * useLiveFetch — GET a public endpoint and keep it in sync with the Admin panel.
 *   • adds a cache-busting query param so no proxy/browser serves stale data
 *   • refetches when the tab regains focus / becomes visible again
 *   • exposes { data, loading, error, reload } so pages can render proper states
 *
 * The Admin portal is the single source of truth — any create/edit/delete
 * there is picked up by the public site the next time it loads or refocuses.
 */
export function useLiveFetch(path, { initial = null, deps = [] } = {}) {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sep = path.includes("?") ? "&" : "?";
      const { data: res } = await api.get(`${path}${sep}_t=${Date.now()}`);
      if (mounted.current) setData(res);
    } catch (e) {
      if (mounted.current) setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  useEffect(() => {
    mounted.current = true;
    load();
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted.current = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  return { data, loading, error, reload: load };
}
